import type I18N from '@amp/web-apps-localization';
import type { Logger, LoggerFactory } from '@amp/web-apps-logger';
import type {
    MetricsFields,
    PageMetrics,
} from '@jet/environment/types/metrics';
import type { ActionModel } from '@jet/environment/types/models';
import type { Opt } from '@jet/environment/types/optional';
import type { Runtime } from '@jet/environment/runtime';
import type { Intent, IntentReturnType } from '@jet/environment/dispatching';
import {
    type ActionImplementation,
    ActionDispatcher,
    type ActionOutcome,
    type MetricsBehavior,
    MetricsPipeline,
} from '@jet/engine';
import { bootstrap } from '@jet-app/music-classical/bootstrap';
import { getContext } from 'svelte';
import type {
    Bag,
    Bundle,
    SubscriptionStatusCoordinator,
} from '~/jet/dependencies';
import { makeDependencies } from '~/jet/dependencies';
import type { Page, Action } from '~/jet/models';
import { PrefetchedIntents } from '@amp/web-apps-common/src/jet/prefetched-intents';
import { Locale } from '~/utils/locale';
import { Metrics } from '@amp/web-apps-metrics-8';
import {
    makeRouteUrlIntent,
    type RouterResponse,
    type RouteUrlIntent,
} from '@jet-app/music-classical/intents/route-url/intent';
import { makeGetStaticCanonicalUrlIntent } from '~/jet/intents';
import type { UserCallbacks } from '~/jet/dependencies/net';
import { config } from '~/config/metrics';
import {
    makeMetricsSettings,
    makeProcessEventFn,
} from '~/jet/metrics/settings';

import { PageContextFieldsProvider } from '~/jet/metrics/providers/PageContextFieldsProvider';
import { StorefrontFieldsProvider } from '~/jet/metrics/providers/StorefrontFieldsProvider';
import type { User } from '~/utils/user';

const CONTEXT_NAME = 'jet';

export interface JetRoute {
    intent: Intent<Page>;
    action: Action;
    storefront: string;
    language: string;
}

/**
 * The entry point for interacting with the Jet shared business logic.
 */
export class Jet {
    private readonly log: Logger;
    private readonly runtime: Runtime;
    private readonly actionDispatcher: ActionDispatcher;
    private readonly bag: Bag;
    private readonly bundle: Bundle;
    private readonly metrics: Metrics;
    private readonly subscriptionStatusCoordinator: SubscriptionStatusCoordinator;

    /**
     * Intents (and their resolved data) that have yet to be dispatched that
     * were recently dispatched. These are consulted before dispatching
     * intents. If a prefetched intent exists for an ongoing dispatch, it will
     * be used as the return value instead of actually dispatching.
     *
     * This can be used, for example, for intents that are dispatched during
     * SSR. The server can serialize the intents it dispatches and then the
     * client can populate this, to avoid re-dispatching the intents.
     */
    private readonly prefetchedIntents: PrefetchedIntents;

    /**
     * A set of the action types that already have registered implementations to catch
     * double registers.
     */
    private readonly wiredActions: Set<string>;

    static load(
        loggerFactory: LoggerFactory,
        context: Map<string, unknown>,
        fetch: typeof window.fetch,
        userCallbacks: UserCallbacks,
        origin: string,
        prefetchedIntents: PrefetchedIntents = PrefetchedIntents.empty(),
        defaultStorefront?: string,
    ): Jet {
        const dependencies = makeDependencies(
            loggerFactory,
            fetch,
            origin,
            userCallbacks,
            defaultStorefront,
        );
        const { subscriptionStatusCoordinator } = dependencies;
        const runtime = bootstrap(dependencies);
        let jet: Jet;

        const metricsSettings = makeMetricsSettings(context);
        const processEvent = makeProcessEventFn();

        const metrics = Metrics.load(
            loggerFactory,
            context,
            processEvent,
            config,
            [
                {
                    provider: new StorefrontFieldsProvider(context),
                    request: 'storeFrontCountryCode',
                },
                {
                    provider: new PageContextFieldsProvider(),
                    request: 'pageContext',
                },
            ],
            metricsSettings,
        );

        const actionDispatcher = new ActionDispatcher(
            metrics.metricsPipeline as unknown as MetricsPipeline,
        );

        const { bag, bundle } = dependencies;

        jet = new Jet(
            loggerFactory.loggerFor('Jet'),
            runtime,
            actionDispatcher,
            bag,
            bundle,
            metrics,
            prefetchedIntents,
            subscriptionStatusCoordinator,
        );

        context.set(CONTEXT_NAME, jet);

        return jet;
    }

    constructor(
        log: Logger,
        runtime: Runtime,
        actionDispatcher: ActionDispatcher,
        bag: Bag,
        bundle: Bundle,
        metrics: Metrics,
        prefetchedIntents: PrefetchedIntents,
        subscriptionStatusCoordinator: SubscriptionStatusCoordinator,
    ) {
        this.log = log;
        this.runtime = runtime;
        this.actionDispatcher = actionDispatcher;

        this.bag = bag;
        this.bundle = bundle;
        this.metrics = metrics;

        this.prefetchedIntents = prefetchedIntents;

        this.subscriptionStatusCoordinator = subscriptionStatusCoordinator;
        this.wiredActions = new Set();
    }

    async didEnterPage(page: Page | null): Promise<void> {
        await this.metrics.didEnterPage(page);
    }

    get pageMetrics(): Opt<PageMetrics> {
        return this.metrics.currentPageMetrics?.pageMetrics;
    }

    /**
     * Dispatch a Jet intent, returning its output.
     *
     * @param intent The intent to dispatch
     * @return output The value returned by the intent's controller
     */
    async dispatch<I extends Intent<unknown>>(
        intent: I,
    ): Promise<IntentReturnType<I>> {
        const data = this.prefetchedIntents.get(intent);
        if (data) {
            this.log.info(
                're-using prefetched intent response for:',
                intent,
                'data:',
                data,
            );
            return data;
        }

        // TODO: rdar://73165545 (Error Handling Across App)
        return this.runtime.dispatch(intent);
    }

    /**
     * Get a canonical URL for a Jet intent, if immediately available.
     * If getting the canonical would be asynchronous (ie. requires a network
     * fetch), `undefined` is returned.
     */
    async getStaticCanonicalUrl<I extends Intent<unknown>>(
        intent: I,
    ): Promise<string | undefined> {
        return this.dispatch(makeGetStaticCanonicalUrlIntent({ intent }));
    }

    /**
     * Perform a Jet action, returning the outcome.
     *
     * @param action The action to perform
     * @param metricsBehavior Indicates how to handle metrics for this action
     * @return outcome Either 'performed' or 'unsupported'
     */
    async perform(
        action: ActionModel,
        metricsBehavior?: MetricsBehavior,
    ): Promise<ActionOutcome> {
        if (!metricsBehavior) {
            if (this.pageMetrics) {
                metricsBehavior = {
                    behavior: 'fromAction',
                    context: this.pageMetrics || {},
                };
            } else {
                this.log.warn(
                    'No pageMetrics found for jet.perform action:',
                    action,
                );
                metricsBehavior = { behavior: 'notProcessed' };
            }
        }
        // TODO: rdar://73165545 (Error Handling Across App): handle throw
        const outcome = await this.actionDispatcher.perform(
            action,
            metricsBehavior,
        );

        if (outcome === 'unsupported') {
            this.log.error(
                'unable to perform action:',
                action,
                metricsBehavior,
            );
        }

        return outcome;
    }

    /**
     * Register an implementation to handle a Jet action.
     *
     * @param kind The type of the action
     * @param implementation The code to run when that action is performed
     */
    onAction<A extends ActionModel>(
        kind: string,
        implementation: ActionImplementation<A>,
    ): void {
        if (this.wiredActions.has(kind)) {
            throw new Error(
                `onAction called twice with the same action type: ${kind}`,
            );
        }

        this.actionDispatcher.register(kind, implementation);
        this.wiredActions.add(kind);
    }

    /**
     * Route a URL using Jet, returning the routing if the URL could be routed.
     *
     * @param url The URL to route
     * @return routing The routing of the URL or null if unrouteable
     */
    async routeUrl(url: string): Promise<RouterResponse | null> {
        const u = new URL(url);
        const canonical = new URL(
            u.pathname + u.search,
            'https://classical.music.apple.com',
        );

        // TODO: rdar://73165545 (Error Handling Across App): what about 404s?
        const routerResponse = await this.dispatch<RouteUrlIntent>(
            makeRouteUrlIntent({ url: canonical.toString() }),
        );

        if (routerResponse && routerResponse.action) {
            const { intent, action, storefront, language } = routerResponse;
            return {
                intent,
                action,
                ...Locale.normalize({ storefront, language }),
            };
        }
        this.log.warn(
            'url did not resolve to a flow action with a discernable intent:',
            url,
            canonical.toString(),
            routerResponse,
        );

        return null;
    }

    setLocale(localizer: I18N, storefront: string, language: string): void {
        this.bundle.setLocalizationKeys(localizer.currentKeys);
        this.bag.setLocale(storefront, language);
    }

    updateUserInfo(user: User): void {
        this.subscriptionStatusCoordinator.updateUserInfo(user);
        if (user?.subscriberType === 'premium') {
            this.disableFunnelKit();
        } else {
            this.enableFunnelKit();
        }
    }

    isUserInHomeStorefront(): boolean {
        return this.subscriptionStatusCoordinator.isUserInHomeStorefront;
    }

    recordCustomMetricsEvent(fields?: Opt<MetricsFields>) {
        this.metrics.recordCustomEvent(fields);
    }

    enableFunnelKit(): void {
        this.metrics.enableFunnelKit();
    }

    disableFunnelKit(): void {
        this.metrics.disableFunnelKit();
    }

    // TODO: rdar://75011660 (Bridge Jet to MetricsKit and PerfKit for reporting)
}

/**
 * Gets the current Jet instance from the Svelte context.
 *
 * @return jet The current instance of Jet
 */
export function getJet(): Jet {
    const jet = getContext(CONTEXT_NAME);

    if (!jet) {
        throw new Error('getJet called before Jet.load');
    }

    return jet as unknown as Jet;
}

/**
 * Jet helper to expose jet.perform in single location
 *
 * @return Promise<ActionOutcome>
 */
type ActionUndefined = 'noActionProvided';
export type JetPerform = Promise<ActionOutcome | ActionUndefined>;

export function getJetPerform(): (action: ActionModel) => JetPerform {
    const jet = getJet();

    return (action: ActionModel) => {
        if (!action) {
            //TODO: rdar://73165545 (Error Handling Across App)
            return Promise.resolve('noActionProvided');
        }

        return jet.perform(action);
    };
}
